import React, { useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useBusiness } from 'hooks/useBusiness';
import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useLocalization } from 'hooks/useLocalization';
import { useUI } from 'hooks/useUI';

import ActivityGrid from 'components/activity-display/layouts/ActivityGrid';
import ActivitiesFullWidth from 'components/activity-display/layouts/ActivitiesFullWidth';
import ActivityDialog from 'components/activity-display/ActivityDialog';
import ControlHeaderContainer from 'components/activity-display/control-header/ControlHeaderContainer';
import { Alert } from '@mui/material'; // Grid version 2
import { Activity, ActivityCategory } from 'interfaces/ActivityInterfaces';
import { LayoutType } from 'interfaces/BusinessInterfaces';

function ActivitiesContent() {
	const navigate = useNavigate();
	const { activities, activityCategories, getBizConfigVar, setPageTitle } = useBusiness();
	const { selectedActivityId, setSelectedActivityId } = useActivityShopUI();
	const { getStr } = useLocalization();
	const { initialTitle } = useUI();
	
	const { activityId: activityIdParam } = useParams<{ activityId?: string }>();
	const activityIdOrNaN = activityIdParam !== undefined ? parseInt(activityIdParam, 10) : NaN; // Attempt to parse only if activityIdParam is not undefined
	const activityIdFromUrl = !isNaN(activityIdOrNaN) ? activityIdOrNaN : null; // JavaScript treats NaN as falsy in boolean contexts, but we need to explicitly check for NaN because the nullish coalescing operator (??) does not.

	const location = useLocation();
	const selectedActivity = activities.find(activity => activity.id === selectedActivityId) || null;

	const layout = getBizConfigVar('layout');
	const useSeparateCategories = getBizConfigVar('useSeparateCategories');
	
	setPageTitle(initialTitle, true);

	useEffect(() => {
		if (activityIdFromUrl === null && selectedActivityId !== null) {
			setSelectedActivityId(null);
		} else {
			setSelectedActivityId(activityIdFromUrl);
		}
	}, [location]);

	function handleClose() {
		navigate('/');

		if (window.location.href.endsWith('/#/')) {
			// Use window.history.replaceState to modify the URL, removing '/#/'
			const newUrl = window.location.href.replace(/\/#\/$/, '/');
			window.history.replaceState(null, '', newUrl);
		}
	};

	function renderLayout(layout: LayoutType, activities: Activity[], activityCategory: ActivityCategory | null = null) {
		switch (layout) {
			case 'tiles':
			case 'cards':
				return (
					<>
						<ActivityGrid activities={activities} layout={layout} activityCategory={activityCategory} />
					</>
				);

			case 'full-width':
				return (
					<>
						<ActivitiesFullWidth activities={activities} activityCategory={activityCategory} />
					</>
				);

			default:
				return <Alert severity="error">Invalid layout: "{layout}"</Alert>;
		}
	}

	const grayBackgroundLayouts = [
		'full-width',
		'cards'
	];

	const dialogLayouts = [
		'tiles',
		'cards'
	];

	return (
		<div style={{ backgroundColor: '#fbfbfb' }}>
			<ControlHeaderContainer />
			{useSeparateCategories ? (
				<>
					{
						activityCategories.map(category => {
							const categoryActivities = activities.filter(activity => activity.categoryId === category.id);
							
							if (categoryActivities.length === 0) return null;
							
							return (
								<React.Fragment key={category.id}>
									{renderLayout(layout, categoryActivities, category)}
								</React.Fragment>
							);
						})
					}
					{
						activities.filter(activity => activity.categoryId === null).length > 0 ? (
							<>
								{renderLayout(layout, activities.filter(activity => activity.categoryId === null), { id: null, title: getStr('ACTIVITY_CATEGORY_Other'), descShort: null })}
							</>
						) : null
					
					}
				</>
			) : (
				renderLayout(layout, activities)
			)}
			{
			//	(((layout as LayoutType) === 'tiles' || (layout as LayoutType) === 'cards') && selectedActivity !== null) && (
				(dialogLayouts.includes(layout) && selectedActivity !== null) && (
					<ActivityDialog selectedActivity={selectedActivity} handleClose={handleClose} />
				)
			}
		</div>
	);
}

export default ActivitiesContent;